import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Upload, FileSpreadsheet, Download, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/common";
import { useApi, errMsg, downloadBlob } from "@/lib/api";
import { dateTime, money } from "@/lib/format";

const FILES = [
  { key: "tape", label: "Loan tape (Excel)", hint: "Hojas: Loan data, Borrower data, Actual Payment data" },
  { key: "conc", label: "Conciliación de comprobantes (Excel)", hint: "Hoja: Comprobantes" },
  { key: "movimientos", label: "Movimientos nuevos del banco / STP (Excel)", hint: "Usa la plantilla" },
];

function FilePick({ f, file, onPick }) {
  return (
    <label data-testid={`update-file-${f.key}`} className="flex min-w-0 cursor-pointer items-center gap-3 rounded-xl border border-dashed border-slate-300 p-4 transition-colors hover:border-teal-500 hover:bg-teal-50/40">
      <FileSpreadsheet className="h-5 w-5 shrink-0 text-teal-700" />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-slate-800">{f.label}</div>
        <div className="truncate text-xs text-slate-500">{file ? file.name : f.hint}</div>
      </div>
      <input type="file" accept=".xlsx" className="hidden" data-testid={`update-input-${f.key}`} onChange={(e) => onPick(e.target.files[0])} />
    </label>
  );
}

function JobCard({ j }) {
  const r = j.resultado || {};
  const icon = j.estado === "listo" ? <CheckCircle2 className="h-4 w-4 text-teal-600" />
    : j.estado === "error" ? <AlertTriangle className="h-4 w-4 text-red-600" /> : <Loader2 className="h-4 w-4 animate-spin text-slate-500" />;
  return (
    <div data-testid={`update-job-${j.id}`} className="space-y-3 rounded-xl border border-slate-200 p-4">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        {icon}<span className="font-medium" data-testid="update-job-status">{j.paso}</span>
        <span className="text-slate-400">· {dateTime(j.created_at)} · {Object.values(j.nombres || {}).join(", ")}</span>
      </div>
      {j.error && <div className="text-sm text-red-600" data-testid="update-job-error">{j.error}</div>}
      {j.resultado && (
        <div className="grid grid-cols-2 gap-3 text-sm md:grid-cols-4">
          <div><div className="text-xs text-slate-500">Saldo SOFOM</div><div className="font-semibold" data-testid="update-saldo-sofom">{money(r.saldo_sofom)}</div></div>
          <div><div className="text-xs text-slate-500">Clientes</div><div className="font-semibold">{r.clientes}</div></div>
          <div><div className="text-xs text-slate-500">Movimientos SOFOM</div><div className="font-semibold">{r.movimientos_sofom}</div></div>
          <div><div className="text-xs text-slate-500">Comprobantes</div><div className="font-semibold">{j.comprobantes ?? "…"}</div></div>
        </div>
      )}
      {j.resultado && (
        <div className="flex flex-wrap gap-2 text-xs">
          {Object.entries(r.cambios || {}).map(([k, v]) => <span key={k} className="rounded-full bg-slate-100 px-2.5 py-1 text-slate-700">{k}: {v}</span>)}
        </div>
      )}
      {j.zip_token && (
        <a href={`/descarga/${j.zip_token}`} target="_blank" rel="noreferrer" data-testid="update-zip-link" className="inline-flex items-center gap-2 text-sm font-medium text-teal-700 hover:underline">
          <Download className="h-4 w-4" /> Descargar ZIP de comprobantes + reporte de cambios
        </a>
      )}
    </div>
  );
}

export default function Updates() {
  const api = useApi();
  const [files, setFiles] = useState({});
  const [jobs, setJobs] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/updates").then((r) => setJobs(r.data)), [api]);
  const running = jobs.some((j) => ["en_cola", "procesando"].includes(j.estado));
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!running) { return undefined; }
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [running, load]);
  const submit = async () => {
    const fd = new FormData();
    Object.entries(files).forEach(([k, f]) => f && fd.append(k, f));
    setBusy(true);
    try {
      await api.post("/updates", fd);
      toast.success("Actualización iniciada");
      setFiles({});
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <>
      <PageHeader title="Actualizar datos" subtitle="Sube el tape, la conciliación o movimientos nuevos: se concilia todo y se regeneran comprobantes" />
      <div className="space-y-6 rounded-2xl border border-slate-200 bg-white p-6">
        <div className="grid gap-3 md:grid-cols-3">
          {FILES.map((f) => <FilePick key={f.key} f={f} file={files[f.key]} onPick={(x) => setFiles((s) => ({ ...s, [f.key]: x }))} />)}
        </div>
        <p className="text-xs text-slate-500">El tape manda: los pagos y dispersiones se ajustan al tape; si la cuenta de la SOFOM no alcanza, se agrega un fondeo automático. Si no subes un archivo, se usa la última versión cargada. Antes de cada actualización se respaldan los datos y, si algo falla, se restauran.</p>
        <div className="flex flex-wrap gap-3">
          <Button data-testid="update-submit-btn" disabled={busy || running || !Object.values(files).some(Boolean)} onClick={submit} className="bg-teal-700 hover:bg-teal-800">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />} Actualizar y conciliar
          </Button>
          <Button variant="outline" data-testid="update-template-btn" onClick={() => downloadBlob(api, "/updates/plantilla-movimientos.xlsx", "Plantilla_movimientos_ePlata.xlsx")}>
            <Download className="mr-2 h-4 w-4" /> Plantilla de movimientos
          </Button>
        </div>
      </div>
      <div className="mt-6 space-y-3" data-testid="update-jobs-list">
        <h2 className="text-base font-semibold text-slate-800 md:text-lg">Historial de actualizaciones</h2>
        {jobs.length === 0 && <div className="text-sm text-slate-500">Aún no hay actualizaciones.</div>}
        {jobs.map((j) => <JobCard key={j.id} j={j} />)}
      </div>
    </>
  );
}
